import { useEffect, useRef } from "react";
import { useFilterStringParams } from "hooks/useFilterStringParams.hook";
import { CONTEXT_TYPES } from "api/bookAppointmentOnline/get-book-appointment-dashboard-items";
import { BookAppointmentOnlineOptionItemModel } from "models/BookAppointmentOnlineModel/BookAppointmentOnlineOptionItemModel";
import { AppointmentTypeOptionProps } from "helpers/get-appointment-type-list-option.helper";

type Params = {
    specialistListData: BookAppointmentOnlineOptionItemModel[] | undefined;
    optionsService: AppointmentTypeOptionProps[] | undefined;
    setValue: (name: string, value: unknown) => void;
    setContext: (context: CONTEXT_TYPES) => void;
};

// Preselects the specialist/service from the `?specialist=` / `?service=` GET params, if present.
// Only commits a value once it's confirmed to exist on the backend; when both params are given,
// the specialist is only selected once the doctor list (re-filtered by the applied service —
// the reliable filter direction) confirms he actually offers it.
export function useWidgetPresetSelection({
    specialistListData,
    optionsService,
    setValue,
    setContext,
}: Params) {
    const [{ specialist: specialistParam, service: serviceParam }] = useFilterStringParams(["specialist", "service"]);
    const presetPhase = useRef<"idle" | "awaiting-cross-check" | "done">("idle");
    const listSnapshotBeforeCrossCheck = useRef<BookAppointmentOnlineOptionItemModel[] | undefined>(undefined);

    const applyServiceMatch = (matchedService: AppointmentTypeOptionProps) => {
        setValue("typeId", matchedService.value);
    };

    // Phase 1: validate both params against the unfiltered lists (doctorId/typeId are still unset here).
    useEffect(() => {
        if (presetPhase.current !== "idle" || (!specialistParam && !serviceParam)) {
            return;
        }

        const specialistReady = !specialistParam || !!specialistListData;
        const serviceReady = !serviceParam || !!optionsService;

        if (!specialistReady || !serviceReady) {
            return;
        }

        const matchedSpecialist = specialistParam
            && specialistListData?.find((specialist) => specialist.getId() === specialistParam);
        const matchedService = serviceParam
            && optionsService?.find((v) => v.value === serviceParam);

        if (matchedSpecialist && matchedService) {
            // Both look valid individually, but the specialist might not actually offer this service.
            // Apply the service now and wait for the doctor list (re-filtered by it) to confirm
            // the specialist before selecting him — the doctor-list filter is the reliable one,
            // unlike the service-list filter by doctorId which the backend doesn't actually apply.
            presetPhase.current = "awaiting-cross-check";
            // Capture the list reference as it is *before* applying the service, so phase 2 can
            // tell apart the still-unfiltered list (this same effect flush) from the actually
            // re-filtered one that arrives once the doctor-list query refetches keyed by typeId.
            listSnapshotBeforeCrossCheck.current = specialistListData;
            applyServiceMatch(matchedService);
            setContext(CONTEXT_TYPES.CONTEXT_SERVICE);
            return;
        }

        presetPhase.current = "done";

        if (matchedService) {
            applyServiceMatch(matchedService);
            setContext(CONTEXT_TYPES.CONTEXT_SERVICE);
        } else if (matchedSpecialist) {
            setValue("doctorId", specialistParam);
            setContext(CONTEXT_TYPES.CONTEXT_DOCTOR);
        }

    }, [specialistParam, serviceParam, specialistListData, optionsService]);

    // Phase 2: once the doctor list filtered by the just-applied service arrives, confirm the
    // requested specialist actually offers it before selecting him in the specialist select.
    useEffect(() => {
        if (
            presetPhase.current !== "awaiting-cross-check"
            || !specialistListData
            || specialistListData === listSnapshotBeforeCrossCheck.current
        ) {
            return;
        }

        presetPhase.current = "done";

        const compatibleSpecialist = specialistListData.find(
            (specialist) => specialist.getId() === specialistParam
        );

        if (compatibleSpecialist) {
            setValue("doctorId", specialistParam);
            setContext(CONTEXT_TYPES.CONTEXT_DOCTOR);
        }
        // otherwise leave the specialist select empty — he doesn't offer the requested service.
    }, [specialistListData, specialistParam]);
}
